import {
  FileModel, UserModel, CustomerGroupModel,
  PartnerShopModel, PartnerProductModel, PartnerProductUnitModel, 
} from '.';
import { unescape } from 'html-escaper';
import { translate, formatNumber } from '../helpers/frontend';
import { SYSTEM_LANGUAGES } from '../actions/variables';

/*
  channel : String
    C2U
    STORE

  type : Number
    1 = Product
    2 = Coupon

  recurringType : Number
    1 = Every X Days
    2 = Every X Months
    3 = Every X Years

  cashDiscountType : Number
    1 = Customer Birth Month
    2 = CashDiscountRules
    3 = Customer Birth Month + CashDiscountRules

  status
    0 = Inactive
    1 = Coming soon
    2 = Active
    4 = New
    5 = Featured
    6 = On Sales
*/

export class PartnerProductSubscriptionModel {
  constructor(data={}) {
    this._id = data?._id || null;
    this.channel = data.channel? unescape(data.channel): null;
    this.type = Number(data.type || 1);

    this.name = data.name? unescape(data.name): null;
    this.description = data.description? unescape(data.description): null;
    this.content = data.content? unescape(data.content): null;
    this.agreement = data.agreement? unescape(data.agreement): null;

    this.priceInVAT = data.priceInVAT? Number(data.priceInVAT): null;
    this.discountPriceInVAT = data.discountPriceInVAT? Number(data.discountPriceInVAT): null;
    this.discountStartAt = data.discountStartAt? data.discountStartAt: null;
    this.discountEndAt = data.discountEndAt? data.discountEndAt: null;
    
    this.recurringType = data.recurringType? Number(data.recurringType): 2;
    this.recurringInterval = data.recurringInterval? Number(data.recurringInterval): 1;
    this.recurringCount = data.recurringCount? Number(data.recurringCount): 12;
    this.recurringSelections = (data?.recurringSelections || []).map(d => this.recurringSelection(d));
    
    this.image = new FileModel(data.image || {});
    this.gallery = data.gallery?.length? data.gallery.map(d => new FileModel(d)): [];

    this.hasRelatedProducts = Number(data.hasRelatedProducts || 0);
    this.relatedCredit = data.relatedCredit ?? 1;
    this.relatedProducts = (data.relatedProducts || []).map(d => this.relatedProduct(d));

    this.hasFreeProducts = Number(data.hasFreeProducts || 0);
    this.freeCredit = data.freeCredit?? 1;
    this.freeProducts = (data.freeProducts || []).map(d => this.freeProduct(d));

    this.hasGiveawayProducts = Number(data.hasGiveawayProducts || 0);
    this.giveawayProducts = (data.giveawayProducts || []).map(d => this.giveawayProduct(d));

    this.selectionSteps = (data.selectionSteps || []).map(d => this.selectionStep(d));

    this.hasCashDiscount = Number(data.hasCashDiscount || 0);
    this.cashDiscount = data.cashDiscount? Number(data.cashDiscount): null;
    this.cashDiscountBirthMonth = data.cashDiscountBirthMonth? Number(data.cashDiscountBirthMonth): null;
    this.cashDiscountType = Number(data.cashDiscountType || 1);
    this.cashDiscountRules = (data.cashDiscountRules || []).map(d => this.cashDiscountRule(d));

    this.forCustomerGroups = (data.forCustomerGroups || []).map(d => new CustomerGroupModel(d));

    this.forAllPartnerShops = Number(data.forAllPartnerShops || 0);
    this.forPartnerShops = (data.forPartnerShops || []).map(d => new PartnerShopModel(d));

    this.freeShippingCost = Number(data.freeShippingCost || 1);
      
    this.order = data.order? data.order: 1;
    this.status = data.status ?? 0;

    this.createdBy = new UserModel(data.createdBy || {});
    this.createdAt = data.createdAt || null;
    this.updatedBy = new UserModel(data.updatedBy || {});
    this.updatedAt = data.updatedAt || null;

    // Localization
    this.localization(data);
  }
  localization(data){
    let self = this;
    self.localizedKeys = [ 'name', 'description', 'content' ];
    SYSTEM_LANGUAGES.forEach(lang => {
      self.localizedKeys.forEach(k => {
        let key = `${k}${lang}`;
        self[key] = data[key]? unescape(data[key]): data[k];
      });
    });
  }

  isValid(){ return this._id? true: false; }

  recurringSelection(d={}){
    return {
      name: d.name || '',
      recurringType: d.recurringType || 2,
      recurringInterval: d.recurringInterval || 1,
      recurringCount: d.recurringCount || 3,
      discountPriceInVAT: Number(d.discountPriceInVAT ?? 0),
    };
  }

  relatedProduct(d={}){
    return {
      _id: d._id || null,
      product: new PartnerProductModel(d.product || {}),
      productId: d.product?._id,
      unit: new PartnerProductUnitModel(d.unit || {}),
      unitId: d.unit?._id,
      credit: d.credit ?? 1,
      quantity: d.quantity ?? 1,
      addPriceInVAT: d.addPriceInVAT ?? 0,
      inCart: d.inCart ?? d.quantity ?? 0,
    };
  }
  freeProduct(d={}){
    return {
      _id: d._id || null,
      product: new PartnerProductModel(d.product || {}),
      productId: d.product?._id,
      unit: new PartnerProductUnitModel(d.unit || {}),
      unitId: d.unit?._id,
      credit: d.credit ?? 1,
      quantity: d.quantity ?? 1,
      inCart: d.inCart ?? d.quantity ?? 0,
    };
  }
  giveawayProduct(d={}){
    return {
      _id: d._id || null,
      product: new PartnerProductModel(d.product || {}),
      productId: d.product?._id,
      unit: new PartnerProductUnitModel(d.unit || {}),
      unitId: d.unit?._id,
      quantity: d.quantity ?? 1,
      inCart: d.inCart ?? d.quantity ?? 0,
    };
  }

  selectionStep(d={}){
    return {
      _id: d._id || null,
      step: d.step || null,
      name: d.name? unescape(d.name): null,
      icon: new FileModel(d.icon || {}),
      credit: d.credit ?? 1,
      order: d.order || 1,
      products: d.products?.length? d.products.map(k => {
        return {
          product: new PartnerProductModel(k.product || {}),
          productId: k.product?._id,
          unit: new PartnerProductUnitModel(k.unit || {}),
          unitId: k.unit?._id,
          credit: k.credit ?? 1,
          quantity: k.quantity ?? 1,
          inCart: k.inCart ?? k.quantity ?? 0,
        };
      }): [],
    };
  }

  cashDiscountRule(d={}){
    return {
      name: d.name || null,
      startAt: d.startAt || null,
      endAt: d.endAt || null,
      cashRuleDiscount: Number(d.cashRuleDiscount) || null,
    };
  }

  displayStatus(langObj={}){
    if(!this.isValid()) return (<></>);
    if(this.status === 6) return (<span className="ss-tag bg-p">{translate('text_product_status_6', langObj)}</span>);
    if(this.status === 5) return (<span className="ss-tag bg-info">{translate('text_product_status_5', langObj)}</span>);
    if(this.status === 4) return (<span className="ss-tag bg-p">{translate('text_product_status_4', langObj)}</span>);
    if(this.status === 2) return (<span className="ss-tag bg-success">{translate('text_product_status_2', langObj)}</span>);
    if(this.status === 1) return (<span className="ss-tag bg-gray">{translate('text_product_status_1', langObj)}</span>);
    return (<span className="ss-tag bg-warning">{translate('text_product_status_0', langObj)}</span>);
  }

  displayFrequency(langObj={}){
    if(!this.isValid()) return '';
    let _text = '';
    if(this.type === 2){
      const _min = !this.recurringSelections.length? 1: this.recurringSelections
        .reduce((prev, current) => prev.recurringCount > current.recurringCount? current: prev)
        ?.recurringCount || 1;
      const _max = !this.recurringSelections.length? 1: this.recurringSelections
        .reduce((prev, current) => prev.recurringCount > current.recurringCount? prev: current)
        ?.recurringCount || 1;
      if(_min === _max) _text = translate('For _X_ Months', langObj, { '_X_': _min });
      else _text = translate('For _X_ Months', langObj, { '_X_': `${_min} - ${_max}` });
    }else{
      if(this.recurringType === 1){
        if(this.recurringInterval === 1) _text += translate('Per Day', langObj);
        else _text += translate('Per _X_ Days', langObj, { '_X_': this.recurringInterval });
      }else if(this.recurringType === 2){
        if(this.recurringInterval === 1) _text += translate('Per Month', langObj);
        else _text += translate('Per _X_ Months', langObj, { '_X_': this.recurringInterval });
      }else if(this.recurringType === 3){
        if(this.recurringInterval === 1) _text += translate('Per Year', langObj);
        else _text += translate('Per _X_ Years', langObj, { '_X_': this.recurringInterval });
      }
      if(this.recurringCount === 1) _text += ` ${translate('For 1 Time', langObj)}`;
      else _text += ` ${translate('For _X_ Times', langObj, { '_X_': this.recurringCount })}`;
    }
    return _text;
  }

  displayDiscountRange(){
    if(!this.isValid() || this.type !== 2 || !this.recurringSelections?.length) return '';
    const _min = !this.recurringSelections.length? 0: this.recurringSelections
      .reduce((prev, current) => prev.discountPriceInVAT < current.discountPriceInVAT? prev: current)
      ?.discountPriceInVAT || 0;
    const _max = !this.recurringSelections.length? 0: this.recurringSelections
      .reduce((prev, current) => prev.discountPriceInVAT > current.discountPriceInVAT? prev: current)
      ?.discountPriceInVAT || 0;
    if(_min === _max) return formatNumber(_min, 2, true);
    return `${formatNumber(_min, 2, true)} - ${formatNumber(_max, 2, true)}`;
  }

  displayFreeShippingCost(langObj={}) {
    if(!this.isValid()) return (<span className="ss-tag bg-warning">{translate('No', langObj)}</span>);
    if(this.freeShippingCost === 1) return (<span className="ss-tag bg-success">{translate('Yes', langObj)}</span>);
    return (<span className="ss-tag bg-warning">{translate('No', langObj)}</span>);
  }
}